// src/components/MapboxLocationPicker.jsx
import React, { useState, useEffect, useRef } from "react";

const MapboxLocationPicker = ({
  location,
  onLocationChange,
  address = "",
  height = "400px",
  className = "",
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const [mapboxgl, setMapboxgl] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);

  // Mapbox Access Token - REPLACE WITH YOUR TOKEN
  const MAPBOX_TOKEN =
    import.meta.env.VITE_MAPBOX_TOKEN ||
    "pk.eyJ1IjoibWFsaGV1cmZvcnR1bmEiLCJhIjoiY2xzNGJkeGNmMDF4cDJqbnhnbjF3OXpuNiJ9.Hc8VeDVh2UaxjRF3UUVRKg";

  // Default location (Basel, Switzerland)
  const defaultLocation = {
    lat: 47.5584,
    lng: 7.5733,
  };

  // Load Mapbox GL JS dynamically
  useEffect(() => {
    const loadMapbox = async () => {
      try {
        // Load Mapbox CSS
        if (!document.querySelector('link[href*="mapbox-gl"]')) {
          const link = document.createElement("link");
          link.href =
            "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css";
          link.rel = "stylesheet";
          document.head.appendChild(link);
        }

        // Load Mapbox JS via script tag
        if (!window.mapboxgl) {
          const script = document.createElement("script");
          script.src =
            "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.js";
          script.onload = () => {
            window.mapboxgl.accessToken = MAPBOX_TOKEN;
            setMapboxgl(window.mapboxgl);
            setIsLoaded(true);
          };
          script.onerror = () => {
            throw new Error("Failed to load Mapbox GL JS");
          };
          document.head.appendChild(script);
        } else {
          // Mapbox already loaded
          window.mapboxgl.accessToken = MAPBOX_TOKEN;
          setMapboxgl(window.mapboxgl);
          setIsLoaded(true);
        }
      } catch (err) {
        console.error("Error loading Mapbox:", err);
        setError("Fehler beim Laden der Karte");
      }
    };

    loadMapbox();
  }, [MAPBOX_TOKEN]);

  // Initialize map
  useEffect(() => {
    if (!isLoaded || !mapboxgl || map.current) return;

    const currentLocation =
      location && location.lat && location.lng ? location : defaultLocation;

    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/streets-v12",
        center: [currentLocation.lng, currentLocation.lat],
        zoom: 13,
      });

      // Add navigation controls
      map.current.addControl(new mapboxgl.NavigationControl(), "top-right");

      // Add marker if location exists
      if (location && location.lat && location.lng) {
        addMarker(location.lat, location.lng);
      }

      // Add click event
      map.current.on("click", handleMapClick);

      // Add geocoder for search
      if (mapboxgl.Geocoder) {
        const geocoder = new mapboxgl.Geocoder({
          accessToken: MAPBOX_TOKEN,
          mapboxgl: mapboxgl,
          placeholder: "Adresse suchen...",
          countries: "ch", // Limit to Switzerland
        });

        geocoder.on("result", (e) => {
          const coords = e.result.center;
          handleLocationSelect(coords[1], coords[0]);
        });

        map.current.addControl(geocoder, "top-left");
      }
    } catch (err) {
      console.error("Error initializing map:", err);
      setError("Fehler beim Initialisieren der Karte");
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [isLoaded, mapboxgl, MAPBOX_TOKEN]);

  // Update marker when location prop changes
  useEffect(() => {
    if (!map.current || !location) return;

    if (location.lat && location.lng) {
      addMarker(location.lat, location.lng);
      map.current.flyTo({
        center: [location.lng, location.lat],
        zoom: 15,
      });
    }
  }, [location]);

  const addMarker = (lat, lng) => {
    if (!mapboxgl) return;

    // Remove existing marker
    if (marker.current) {
      marker.current.remove();
    }

    // Add new marker
    marker.current = new mapboxgl.Marker({
      color: "#b3a9d7",
      draggable: true,
    })
      .setLngLat([lng, lat])
      .addTo(map.current);

    // Handle marker drag
    marker.current.on("dragend", () => {
      const coords = marker.current.getLngLat();
      handleLocationSelect(coords.lat, coords.longitude);
    });
  };

  const handleMapClick = (e) => {
    const { lat, lng } = e.lngLat;
    handleLocationSelect(lat, lng);
  };

  const handleLocationSelect = async (lat, lng) => {
    const newLocation = {
      lat: parseFloat(lat.toFixed(6)),
      lng: parseFloat(lng.toFixed(6)),
    };

    addMarker(lat, lng);

    // Gemeinde ermitteln
    const geocodeResult = await handleReverseGeocode(lat, lng);

    if (onLocationChange) {
      onLocationChange(newLocation, geocodeResult?.gemeinde || null);
    }
  };

  const handleClearLocation = () => {
    if (marker.current) {
      marker.current.remove();
      marker.current = null;
    }

    if (onLocationChange) {
      onLocationChange({ lat: null, lng: null });
    }
  };

  const handleCenterOnAddress = async () => {
    if (!address || !mapboxgl) return;

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          address
        )}.json?access_token=${MAPBOX_TOKEN}&country=ch&limit=1`
      );

      const data = await response.json();

      if (data.features && data.features.length > 0) {
        const coords = data.features[0].center;
        const lat = coords[1];
        const lng = coords[0];

        map.current.flyTo({
          center: [lng, lat],
          zoom: 15,
        });

        handleLocationSelect(lat, lng);
      }
    } catch (err) {
      console.error("Geocoding error:", err);
    }
  };

  const handleReverseGeocode = async (lat, lng) => {
    if (!mapboxgl) return null;

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}&types=place,locality,district`
      );

      const data = await response.json();

      if (data.features && data.features.length > 0) {
        // Erste Feature ist meist die genaueste Gemeinde
        const place = data.features[0];
        return {
          gemeinde: place.text,
          fullAddress: place.place_name,
        };
      }
    } catch (err) {
      console.error("Reverse Geocoding error:", err);
    }

    return null;
  };

  if (error) {
    return (
      <div className="mapbox-error">
        <p>{error}</p>
        <p>Bitte überprüfen Sie Ihre Mapbox-Konfiguration.</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="mapbox-loading" style={{ height }}>
        <p>Karte wird geladen...</p>
      </div>
    );
  }

  return (
    <div className={`mapbox-location-picker ${className}`}>
      {/* Controls */}
      <div className="mapbox-controls">
        <div className="control-group">
          {address && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleCenterOnAddress}
              title="Adresse auf Karte anzeigen"
            >
              📍 Adresse finden
            </button>
          )}

          {location && location.lat && location.lng && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleClearLocation}
              title="Standort entfernen"
            >
              🗑️ Entfernen
            </button>
          )}
        </div>

        {/* Coordinates Display */}
        {location && location.lat && location.lng && (
          <div className="coordinates-display">
            <small>
              {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
            </small>
          </div>
        )}
      </div>

      {/* Map Container */}
      <div ref={mapContainer} className="mapbox-container" style={{ height }} />

      {/* Instructions */}
      <div className="mapbox-instructions">
        <small>
          💡 Klicken Sie auf die Karte oder ziehen Sie den Marker, um den
          Standort zu setzen
        </small>
      </div>
    </div>
  );
};

export default MapboxLocationPicker;
